import { z } from "zod";

/**
 * Worker sign-off PIN — mobile portal surface (#1773 / MP-29).
 *
 * The web (magic-token) surface carries the PIN inline in each sign-off
 * request and proof in a cookie; the app instead has a dedicated per-project
 * endpoint (GET status / POST enrol / POST verify) and presents the returned
 * proof token in the `x-signoff-pin-proof` header on the gated sign-off
 * routes. These are the wire shapes for that surface.
 *
 * Strictness policy (#2411): the STATUS response is display data the client
 * parses permissively (safeParseOrWarn); the VERIFY response is a WRITE
 * CONTROL — it carries the proof token that authorises sign-offs — so it is
 * strict and the client parses it through the required-response boundary
 * (parseRequiredWireResponse), which strips additive keys but throws on a
 * missing or malformed control. REQUEST schemas are strict; the server
 * validates input it controls.
 */

/** Header the gated mobile sign-off routes read proof-of-PIN from. */
export const MOBILE_SIGNOFF_PIN_PROOF_HEADER = "x-signoff-pin-proof";

/** Does this project ask for a sign-off PIN, and where is this worker with it? */
export const SignoffPinStatusSchema = z.object({
  /** The 3-state resolution (project override over company default), below-Business standing down. */
  required: z.boolean(),
  /** Whether THIS worker has enrolled a PIN yet. */
  enrolled: z.boolean(),
  /** Non-null while the worker is locked out of PIN entry. */
  lockedUntil: z.string().datetime().nullable(),
  /**
   * Server-minted tamper-proof assertion of this not-required answer,
   * present ONLY when `required` is false (MP-29 review round 2). The
   * device stores it and an offline capture presents it in the mutation
   * body (`pinPolicyToken`) so the server can verify the exemption AT
   * CAPTURE TIME — a plain cached flag or a client clock proves nothing.
   * Payload is base64url-readable without the secret (offline freshness
   * checks); the HMAC makes it unforgeable.
   */
  policyToken: z.string().nullable(),
});

export type SignoffPinStatus = z.infer<typeof SignoffPinStatusSchema>;

export const SignoffPinStatusResponse = z.object({
  status: SignoffPinStatusSchema,
});

export type SignoffPinStatusResponse = z.infer<typeof SignoffPinStatusResponse>;

/** First-use enrolment: the worker CHOOSES a PIN (only when none is set). */
export const SignoffPinEnrolRequest = z
  .object({
    intent: z.literal("enrol"),
    pin: z.string().min(4).max(32),
  })
  .strict();

export type SignoffPinEnrolRequest = z.infer<typeof SignoffPinEnrolRequest>;

/** Every subsequent entry: the worker ENTERS their existing PIN. */
export const SignoffPinVerifyRequest = z
  .object({
    intent: z.literal("verify"),
    pin: z.string().min(4).max(32),
  })
  .strict();

export type SignoffPinVerifyRequest = z.infer<typeof SignoffPinVerifyRequest>;

/** The POST body the /signoff-pin route accepts (enrol and verify share it). */
export const SignoffPinActionRequest = z.discriminatedUnion("intent", [
  SignoffPinEnrolRequest,
  SignoffPinVerifyRequest,
]);

export type SignoffPinActionRequest = z.infer<typeof SignoffPinActionRequest>;

/**
 * Proof-of-PIN minted by a successful enrol/verify: an opaque, signed,
 * project-scoped token plus its absolute expiry. STRICT — a write control.
 */
export const SignoffPinVerifyResponse = z
  .object({
    proof: z.string().min(1),
    expiresAt: z.string().datetime(),
  })
  .strict();

export type SignoffPinVerifyResponse = z.infer<typeof SignoffPinVerifyResponse>;

// ---------------------------------------------------------------------------
// MANAGER (owner-app) surface — MP-31 / #2296.
//
// The owner-side mirrors of the web routes (`GET,PATCH /api/account/signoff-pin`,
// `GET,PATCH /api/projects/[projectId]/signoff-pin`, `GET,POST
// /api/projects/[projectId]/crew-pin`). Web parity for shape; the mobile
// routes serve these under /api/mobile/v1/* with mobile envelopes.
//
// NO-HASH GUARANTEE. The roster carries ONLY whether a PIN exists — never the
// hash, never the credential fingerprint, never anything derived from the PIN.
// Every schema here is `.strict()`, so a server that ever added a hash-like
// field to the wire would fail the client's parse (and the conformance tests)
// rather than ship silently. `pinHash` itself never leaves the server.
// ---------------------------------------------------------------------------

/**
 * One roster entry: a worker's sign-off PIN state for the manager's crew list.
 * `lockedUntil` is non-null only while a lockout is actually in force.
 */
export const CrewPinMemberSchema = z.object({
  portalUserId: z.string().min(1),
  name: z.string(),
  companyName: z.string().nullable(),
  /** True once the worker has enrolled a PIN — the ONLY fact about it. */
  hasPin: z.boolean(),
  pinSetAt: z.string().datetime().nullable(),
  lockedUntil: z.string().datetime().nullable(),
});
// Server-boundary strict variant (#2411): the mobile app parses the
// permissive base so an installed build tolerates additive server fields;
// the route applies strictness when serialising its response.
export const CrewPinMemberStrict = CrewPinMemberSchema.strict();
export type CrewPinMember = z.infer<typeof CrewPinMemberSchema>;

export const CrewPinRosterResponse = z
  .object({ crew: z.array(CrewPinMemberSchema) })
  .strict();
export type CrewPinRosterResponse = z.infer<typeof CrewPinRosterResponse>;

/** POST body — the ONE manager action (reset clears the PIN and any lockout). */
export const CrewPinResetRequest = z
  .object({ portalUserId: z.string().min(1), action: z.literal("reset") })
  .strict();
export type CrewPinResetRequest = z.infer<typeof CrewPinResetRequest>;

export const CrewPinResetResponse = z.object({ ok: z.boolean() }).strict();
export type CrewPinResetResponse = z.infer<typeof CrewPinResetResponse>;

/** Company default — `GET,PATCH /api/mobile/v1/account/signoff-pin`. */
export const SignoffPinCompanyDefaultSchema = z.object({
  required: z.boolean(),
  /** Business+ (including site_control) — `false` below Business. */
  entitled: z.boolean(),
});
export const SignoffPinCompanyDefaultStrict =
  SignoffPinCompanyDefaultSchema.strict();
export type SignoffPinCompanyDefault = z.infer<typeof SignoffPinCompanyDefaultSchema>;

export const SignoffPinCompanyDefaultPatchRequest = z
  .object({ required: z.boolean() })
  .strict();
export type SignoffPinCompanyDefaultPatchRequest = z.infer<
  typeof SignoffPinCompanyDefaultPatchRequest
>;

/**
 * Per-project override — THREE-STATE (`null` = follow the company default,
 * which is NOT the same as `false`), plus what it inherits and resolves to so
 * the UI can label the inherit option with the live answer.
 */
export const SignoffPinProjectPolicySchema = z.object({
  override: z.boolean().nullable(),
  companyDefault: z.boolean(),
  effective: z.boolean(),
  entitled: z.boolean(),
});
export const SignoffPinProjectPolicyStrict =
  SignoffPinProjectPolicySchema.strict();
export type SignoffPinProjectPolicy = z.infer<typeof SignoffPinProjectPolicySchema>;

export const SignoffPinProjectPolicyPatchRequest = z
  .object({ override: z.boolean().nullable() })
  .strict();
export type SignoffPinProjectPolicyPatchRequest = z.infer<
  typeof SignoffPinProjectPolicyPatchRequest
>;
